import type { ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl, { type FormControlProps } from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import Select, { type SelectChangeEvent } from '@mui/material/Select';
import {
  addToArray,
  filterOutKeyValue,
  mapToKey,
} from '@witmetrics/utils/arrays';
import { deepMerge } from '@witmetrics/utils/objects';
import type { Nullish } from '@witmetrics/utils';
import CheckBox from '@/components/CheckBox';
import type { CheckboxDropdownOption } from './types';
import { buildOption, classes, isSelected, styles } from './utils';

export type { CheckboxDropdownOption };

export { buildOption };

export interface CheckboxDropdownProps<T extends string | number> {
  disabled?: boolean;
  autoFocus?: boolean;
  className?: string;
  menuClassName?: string;
  sx?: FormControlProps['sx'];
  variant?: 'outlined' | 'filled';
  name: string;
  label?: string;
  placeholder?: string;
  value: CheckboxDropdownOption<T>[] | Nullish;
  options?: CheckboxDropdownOption<T>[];
  renderIcon?: (className: string) => ReactNode;
  onChange: (update: CheckboxDropdownOption<T>[]) => void;
}

export default function CheckboxDropdown<T extends string | number>({
  disabled,
  autoFocus,
  className,
  menuClassName,
  sx = {},
  variant = 'outlined',
  name,
  label,
  options = [],
  placeholder,
  value,
  renderIcon,
  onChange,
}: CheckboxDropdownProps<T>) {
  const currentValue = value || [];

  const handleChange = ({ target }: SelectChangeEvent<T[]>) => {
    const values = (target.value as T[]).map(
      (v) => options.find((o) => o.value === v)!
    );
    onChange(values);
  };

  const handleToggle = (option: CheckboxDropdownOption<T>) => {
    // Remove if present, add if not
    onChange(
      isSelected(currentValue, option)
        ? filterOutKeyValue(currentValue, 'value', option.value)
        : addToArray(currentValue, option)
    );
  };

  const selectedValues = mapToKey(currentValue, 'value');

  return (
    <FormControl
      disabled={disabled}
      sx={deepMerge(styles[variant], sx || {})}
      className={className}
      size="small">
      {label && <InputLabel id={`${name}-label`}>{label}</InputLabel>}
      <Select<T[]>
        multiple
        defaultOpen={!disabled && autoFocus}
        labelId={`${name}-label`}
        id={name}
        name={name}
        placeholder={placeholder}
        value={selectedValues}
        MenuProps={{
          slotProps: {
            paper: { className: twMerge(classes.menu, menuClassName) },
          },
        }}
        input={
          <OutlinedInput
            id={`${name}-input`}
            label={label}
            startAdornment={
              value &&
              renderIcon && (
                <InputAdornment position="start">
                  {renderIcon(classes.inputIcon)}
                </InputAdornment>
              )
            }
          />
        }
        renderValue={(val) => val.join(', ')}
        onChange={handleChange}>
        {options.map((option) => (
          <MenuItem
            key={option.value}
            sx={styles.menuItem}
            className={classes.menuItem}
            value={option.value}>
            <div className={classes.start}>
              <CheckBox
                isChecked={selectedValues.includes(option.value)}
                name={option.label}
                onChange={() => handleToggle(option)}
              />
              {option.label}
            </div>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
